import { useMemo, type ReactNode } from 'react'
import {
  CustomFieldCalculator,
  type CustomFieldValue,
  isEmpty,
  type CustomFieldId,
  type UnsavedUser,
} from '@terros/common'
import { type UseFormReturnType } from '@mantine/form'
import { Skeleton } from '@mantine/core'
import { SizePrimitives, useCustomFields } from '@front/model'
import { TAlert } from '../terros'
import { LockedFieldTooltip } from '../permissions'
import { CustomFieldInput } from '../customField'

type Props<T extends UnsavedUser> = {
  form: UseFormReturnType<T>
  disabled?: boolean
}

export const UserCustomFields = <T extends UnsavedUser>({ form, disabled = false }: Props<T>): ReactNode => {
  const { data: customFields, isLoading, error } = useCustomFields('User')
  const setCustomField = (fieldId: CustomFieldId) => (value: CustomFieldValue) => {
    form.setValues((prev) => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [fieldId]: value,
      },
    }))
  }

  const calculator = useMemo(() => {
    return new CustomFieldCalculator(customFields ?? [], form.values.customFields)
  }, [customFields, form.values.customFields])

  if (isLoading) return <Skeleton height={SizePrimitives['12']} />

  if (!customFields || error) {
    return (
      <TAlert title='Error' variant='error'>
        {error ? error : 'Failed to load custom fields'}
      </TAlert>
    )
  }

  if (isEmpty(customFields)) return null

  return (
    <>
      {customFields.map((field) => {
        const error = form.getInputProps(`customFields.${field.fieldId}`).error
        return (
          <LockedFieldTooltip key={field.fieldId} locked={disabled}>
            <CustomFieldInput
              calculator={calculator}
              error={error}
              field={field}
              onChange={setCustomField(field.fieldId)}
              withAsterisk={field.required}
            />
          </LockedFieldTooltip>
        )
      })}
    </>
  )
}
